import React from 'react';
import PropTypes from 'prop-types';
import { EVENT_CATEGORY } from './constants';
import { ActivityIcon, CreditCardIcon } from './lucideIcons';
import style from './index.scss';

/**
 * The Activity / Transaction switch above the type list.
 *
 * NOT antd `Tabs`. The design is a SEGMENTED control — a `role="tablist"` pill
 * with a muted trough and a white raised thumb on the active option — while
 * antd's Tabs is an underline strip. Reaching one from the other means
 * overriding `.ant-tabs-nav`, `-nav-list`, `-tab`, `-tab-active` and
 * `-ink-bar`, every one of which is a specificity fight this codebase has
 * already lost repeatedly. Twenty lines of our own markup is both smaller and
 * exactly right.
 *
 * Measured from the design's DevTools, 2026-09-01:
 *   tablist  inline-flex h-10 items-center justify-center rounded-md bg-muted
 *            p-1 text-muted-foreground
 *   tab      rounded-sm px-3 py-1.5 text-sm font-medium gap-1.5 transition-all
 *   active   bg-background text-foreground shadow-sm
 * with --radius .375rem, so rounded-md is 4px and rounded-sm is 2px.
 *
 * Follows the WAI-ARIA tabs pattern: arrow keys move between options, which
 * Radix gives the design for free and a pair of bare buttons would not.
 */

/* 13px, the size the design renders: these buttons carry no `[&_svg]:size-4`,
   so the svg's own width/height stands — unlike the header actions, where the
   CSS rule overrides the attribute and the glyph lands at 16px. */
const ICON_SIZE = 13;

const OPTIONS = [
  {
    key: EVENT_CATEGORY.ACTIVITY,
    label: 'Based on activity_type',
    Icon: ActivityIcon
  },
  {
    key: EVENT_CATEGORY.TRANSACTION,
    label: 'Based on txn_type',
    Icon: CreditCardIcon
  }
];

const CategoryTabs = ({ value, onChange }) => {
  const move = (event) => {
    const delta =
      { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key] ||
      0;
    if (!delta) return;
    event.preventDefault();
    const at = OPTIONS.findIndex((o) => o.key === value);
    // Wraps, as the ARIA pattern expects.
    const next = OPTIONS[(at + delta + OPTIONS.length) % OPTIONS.length];
    onChange(next.key);
  };

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={style.categoryTabs}
      onKeyDown={move}
    >
      {OPTIONS.map(({ key, label, Icon }) => {
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            /* Only the active tab is in the tab order; the arrow keys reach the
               other one. Two tab stops for one control is the usual mistake. */
            tabIndex={active ? 0 : -1}
            className={`${style.categoryTab} ${
              active ? style.categoryTabActive : ''
            }`}
            onClick={() => onChange(key)}
          >
            <Icon size={ICON_SIZE} />
            {label}
          </button>
        );
      })}
    </div>
  );
};

CategoryTabs.propTypes = {
  value: PropTypes.oneOf(Object.values(EVENT_CATEGORY)).isRequired,
  onChange: PropTypes.func.isRequired
};

export default CategoryTabs;
